/**
 * EndVideoCallMessageUseCase
 *
 * Closes a 1:1 call by flipping the `videoCall` message's `isLive` to `false`
 * and stamping its `callDuration` (milliseconds). Fired from the Agora teardown
 * path in the Task-10 `useVideoCall` hook.
 *
 * Flutter source: lib/presentation/screens/chat/chat_screen.dart:813-840
 *   (`endVideoCallMessage`):
 *     - `firebaseUser == null` -> return `false` (`:819-822`).
 *     - locate the message by `videoCallId == messageId` (`:824-826`); on web the
 *       caller supplies the already-resolved entity (the analog of Flutter's
 *       `_listItems.firstWhereOrNull`).
 *     - **the "allow only one channel close" guard (`:827-828`):** only act when
 *       `message != null && message.isLive!` — set `isLive = false`,
 *       `callDuration = liveDurationMilliseconds`, then persist via
 *       `ChatRepository.saveChatMessage(_e2ee, …)` and return its boolean
 *       (`:829-836`). Otherwise return `false` (`:839`).
 *
 * The guard is **parity-critical**: both endpoints (and the local teardown +
 * the live-subscription `isLive=false` echo) can race to end the same call, so a
 * second invocation after the first close must be a no-op write. Dropping it
 * would re-write the doc (and re-stamp the duration) on every teardown event.
 *
 * The message has empty `content`, so the value handed to
 * `ChatService.saveChatMessage` is the empty string `''`, NOT `null`. Flutter
 * writes `content: ''` / `last_content: ''` for this message
 * (chat_repository.dart:20-25 else-branch + chat_data_source.dart:35); a `null`
 * `last_content` crashes Flutter's non-nullable parse
 * (chat_message_node.dart:42). The service maps `''` to `last_content: ''`.
 *
 * Pure-domain contract: the use case mutates the supplied entity and persists it,
 * returning the service's success boolean; no Redux / UI concerns.
 */

import { ChatService } from '@/domain/services/chat/chatService';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { auth } from '@/data/services/firebase';

export interface EndVideoCallMessageParams {
  /** The call session id (`videoCallId`). Carried for parity / logging. */
  callMessageId: string;
  /** Final call duration in milliseconds, stamped onto `callDuration`. */
  liveDurationMs: number;
  /**
   * The current `videoCall` message entity (the web analog of Flutter's
   * `_listItems.firstWhereOrNull`). `null` when the call message is not yet in
   * the live list -> no-op `false`.
   */
  message: ChatMessageEntity | null;
  /** Chat-users context forwarded to the save path for the metadata doc. */
  chatUsersInfo: ChatUsersInfoEntity;
}

export class EndVideoCallMessageUseCase {
  private chatService: ChatService;

  constructor(chatService: ChatService = ChatService.getInstance()) {
    this.chatService = chatService;
  }

  /**
   * Close the call message once and only once.
   *
   * @returns the `ChatService.saveChatMessage` boolean when the close write
   *   runs; `false` when there is no authenticated user (`:819-822`), no message,
   *   or the message is already not live (the one-close guard, `:827-828`).
   */
  async execute({
    liveDurationMs,
    message,
    chatUsersInfo,
  }: EndVideoCallMessageParams): Promise<boolean> {
    // Parity `chat_screen.dart:819-822` — no authenticated user -> `false`.
    if (auth.currentUser == null) {
      return false;
    }

    // The "allow only one channel close" guard (`chat_screen.dart:827-828`):
    // act only when the message exists AND is still live. A second teardown
    // event (or the live-subscription `isLive=false` echo) is a no-op write.
    if (message != null && message.isLive === true) {
      message.isLive = false;
      message.callDuration = liveDurationMs;
      // Empty content -> empty-string `''` (NOT `null`); the service maps it to
      // `last_content: ''` / `content: ''` (parity chat_data_source.dart:35).
      return this.chatService.saveChatMessage(message, chatUsersInfo, '');
    }

    return false;
  }
}

// Export singleton instance
export const endVideoCallMessageUseCase = new EndVideoCallMessageUseCase();
